import { useMemo, useId, useEffect, useRef } from 'react'

import { Collapsible } from '../Collapsible'

import { useAccordionContext } from './Accordion'
import { AccordionItemProps } from './types'
import { twMerge } from 'tailwind-merge'

export const Item = ({
  title,
  children,
  id: providedId,
  className,
  initialOpen = false,
  onClick,
  ...props
}: AccordionItemProps) => {
  const generatedId = useId()
  const {
    openItems,
    handleClick,
    iconPosition,
    openIcon,
    closedIcon,
    disableToggle,
    divider,
    keepMounted = false,
  } = useAccordionContext()

  const id = providedId ?? generatedId

  const hasMounted = useRef(false)

  const open = useMemo(() => {
    return Boolean(openItems.find((item) => item === id))
  }, [openItems])

  useEffect(() => {
    hasMounted.current = true
    if (initialOpen) {
      handleClick(id, true)
    }
  }, [])

  const getIcon = (open: boolean, disableToggle: boolean | undefined) => {
    if (open) {
      return disableToggle ? null : openIcon
    }
    return closedIcon
  }

  const icon = useMemo(
    () => (
      <div className="accordion__item-icon" aria-hidden="true">
        {getIcon(open, disableToggle)}
      </div>
    ),
    [open, disableToggle],
  )

  const buttonId = `accordion-button:${id}`
  const panelId = `accordion-panel:${id}`

  return (
    <div
      className={twMerge(
        'accordion__item',
        divider && 'accordion__item--divider',
      )}
    >
      <button
        type="button"
        className={twMerge(
          'accordion__item-button',
          `accordion__item-button--icon-${iconPosition}`,
          open && disableToggle && 'accordion__item-button--disabled',
          className,
        )}
        id={buttonId}
        aria-controls={panelId}
        aria-expanded={open}
        onClick={onClick ?? (() => handleClick(id, !open))}
      >
        {iconPosition === 'start' && icon}
        {typeof title === 'string' ? (
          <span dangerouslySetInnerHTML={{ __html: title }} />
        ) : (
          title
        )}
        {iconPosition === 'end' && icon}
      </button>

      <Collapsible
        open={open || (initialOpen && !hasMounted.current)}
        id={panelId}
        aria-labelledby={buttonId}
        transition={{ duration: '0.25s' }}
        keepMounted={keepMounted}
        {...props}
      >
        {children}
      </Collapsible>
    </div>
  )
}
